import { NavLink } from 'react-router-dom'
import DataTable from '../../../common/data-table/DataTable'
import PageHeader from '../../../layout/page/PageHeader'
import dayjs from 'dayjs'
import { FilterMatchMode } from 'primereact/api'
import { Calendar } from 'primereact/calendar'
import { ButtonGroup } from 'react-bootstrap'
import { useState } from 'react'
import DeleteDialog from '../../../common/dialogs/DeleteDialog'
import TrueFalseCell from '../../../common/data-table/TrueFalseCell'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEdit } from '@fortawesome/free-solid-svg-icons'

type TableRow = Record<string, unknown> & { id?: number; name?: string; media_store_id?: number; pages_name?: string; updated_at?: string | null; active?: number }
type DateFilterOptions = { value: unknown; filterCallback: (v: unknown, i: number) => void; index: number }

const PageSections = () => {
    const [refreshTable, setRefreshTable] = useState(false)

    const dateTimeBodyTemplate = (rowData: TableRow) => {
        return rowData.updated_at != null && rowData.updated_at !== undefined
            ? dayjs(rowData.updated_at as string).format('DD-MMM-YYYY @ HH:mm:ss')
            : ''
    }

    const dateFilterTemplate = (options: DateFilterOptions) => {
        return (
            <Calendar
                value={options.value as Date | null | undefined}
                onChange={(e) => options.filterCallback(e.value, options.index)}
                dateFormat="dd/mm/yy"
                placeholder="dd/mm/yyyy"
                mask="99/99/9999"
            />
        )
    }

    const imageBodyTemplate = (rowData: TableRow) => {
        return (
            <>
                {rowData.media_store_id ? (
                    <img
                        src={`${process.env.REACT_APP_API_ROOT_URL}/media/download/${rowData.media_store_id}/42`}
                        alt={rowData.pages_name}
                        className="w-6rem shadow-2 border-round"
                        style={{ maxWidth: 42 }}
                    />
                ) : (
                    <div>
                        <FontAwesomeIcon
                            icon={['far', 'image']}
                            className="text-muted fa-2x"
                        />
                    </div>
                )}
            </>
        )
    }

    const actionsBodyTemplate = (rowData: TableRow) => {
        return (
            <ButtonGroup size="sm">
                <DeleteDialog
                    url={'/page-components/sections/delete/' + rowData.id}
                    record={rowData}
                    deletedCallback={() => setRefreshTable(true)}
                    message={
                        <>
                            <p className="mb-3 pb-3 border-bottom">
                                Are you sure you want to delete page section:
                                <strong> {rowData.name}</strong>?
                            </p>
                        </>
                    }
                />
                <NavLink
                    to={'/content/page-sections/view/' + rowData.id}
                    className="btn btn-outline-primary ms-1"
                >
                    <FontAwesomeIcon icon={faEdit} />
                </NavLink>
            </ButtonGroup>
        )
    }

    const statusTemplate = (rowData: TableRow) => {
        return (
            <TrueFalseCell
                value={rowData.active === 1}
                trueLabel="Active"
                falseLabel="Inactive"
            />
        )
    }

    return (
        <>
            <PageHeader
                icon="layer-group"
                subtitle="Organise content into reusable sections"
                header="Page Sections"
                buttons={[
                    {
                        variant: 'success',
                        label: 'Create New',
                        icon: 'fa-solid fa-plus',
                        url: '/content/page-sections/new',
                    },
                ]}
            />
            <DataTable
                refreshTable={refreshTable}
                onRefreshed={() => setRefreshTable(false)}
                config={{
                    dataRoute: '/page-components/sections',
                    first: 0,
                    rows: 10,
                    page: 0,
                    sortField: 'name',
                    sortOrder: 1,
                    filters: {
                        global: {
                            value: null,
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                        pages_name: {
                            value: '',
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                        name: {
                            value: '',
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                        code: {
                            value: '',
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                        updated_at: {
                            value: '',
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                    },
                    filterDisplay: 'menu',
                }}
                toolbarConfig={{
                    includeSearchBox: true,
                    includeExportBtn: true,
                }}
                columns={[
                    {
                        field: 'media_store_id',
                        header: 'Image',
                        sortable: true,
                        body: imageBodyTemplate,
                        fixedCenter: true,
                        alignHeader: 'center',
                        headerStyle: {
                            width: '6.5rem',
                            minWidth: '6.5rem',
                            maxWidth: '6.5rem',
                        },
                        style: {
                            width: '6.5rem',
                            minWidth: '6.5rem',
                            maxWidth: '6.5rem',
                        },
                    },
                    {
                        field: 'name',
                        header: 'Name',
                        sortable: true,
                        filter: true,
                        filterPlaceholder: 'Search',
                    },
                    {
                        field: 'pages_name',
                        header: 'Page',
                        sortable: true,
                        filter: true,
                        filterPlaceholder: 'Search',
                        fixedCenter: true,
                        alignHeader: 'center',
                        headerStyle: {
                            width: '7.5rem',
                            minWidth: '7.5rem',
                            maxWidth: '7.5rem',
                        },
                        style: {
                            width: '7.5rem',
                            minWidth: '7.5rem',
                            maxWidth: '7.5rem',
                        },
                    },
                    {
                        field: 'updated_at',
                        header: 'Last Updated',
                        sortable: true,
                        filter: true,
                        dataType: 'date',
                        body: dateTimeBodyTemplate,
                        filterElement: dateFilterTemplate,
                        style: {
                            maxWidth: '3rem',
                        },
                    },
                    {
                        field: 'active',
                        header: 'Status',
                        sortable: true,
                        body: statusTemplate,
                        fixedCenter: true,
                        alignHeader: 'center',
                        headerStyle: {
                            width: '7.5rem',
                            minWidth: '7.5rem',
                            maxWidth: '7.5rem',
                        },
                        style: {
                            width: '7.5rem',
                            minWidth: '7.5rem',
                            maxWidth: '7.5rem',
                        },
                    },
                    {
                        field: 'id',
                        header: 'Actions',
                        sortable: false,
                        body: actionsBodyTemplate,
                        alignHeader: 'center',
                        headerStyle: {
                            maxWidth: '1.2rem',
                            textAlign: 'center',
                        },
                        style: {
                            maxWidth: '1.2rem',
                            textAlign: 'center',
                        },
                        toggleVisibility: false,
                    },
                ]}
                globalFilterFields={['name', 'pages_name', 'updated_at']}
            />
        </>
    )
}

export default PageSections
